import Link from 'next/link';
import { RiArrowRightLine } from 'react-icons/ri';

import { ProductCard } from '@/entities/product/product-card';

import type { Product } from '@/shared/api/generated/api.generated';

import { EquipmentImage } from './equipment-image';

export interface DeliveryCase {
  id: string;
  title: string;
  category: string;
  description: string;
  result: string;
  image: string;
  imageAlt: string;
  sourceUrl?: string;
  published: boolean;
}
export interface HomepageContent {
  about: { title: string; lead: string; text: string; image: string; imageAlt: string };
  cases: DeliveryCase[];
  faq: { id: string; question: string; answer: string }[];
  products: Product[];
}
export const HomepageShowcase = ({ products }: { products: Product[] }) =>
  !products.length ? null : (
    <section className="homepage-showcase container" id="equipment">
      <div className="section-heading">
        <div>
          <p className="eyebrow">ВЫБИРАЕМ ДЛЯ ВАШЕГО ПРОИЗВОДСТВА</p>
          <h2>Оборудование в фокусе.</h2>
        </div>
        <p>
          Сравните модели.
          <br />
          Уточните комплектацию и условия поставки.
        </p>
        <Link className="text-link" href="/catalog">
          Весь каталог <RiArrowRightLine />
        </Link>
      </div>
      <div className="homepage-products">
        {products.map((item) => (
          <ProductCard key={item.id} item={item} />
        ))}
      </div>
    </section>
  );
export const HomepageAbout = ({ about }: Pick<HomepageContent, 'about'>) => (
  <section className={`about-section container${about.image ? '' : ' about-section--text'}`} id="about">
    <div className="about-copy">
      <p className="eyebrow">О КОМПАНИИ</p>
      <h2 style={{ whiteSpace: 'pre-line' }}>{about.title}</h2>
      <p className="about-lead">{about.lead}</p>
      <p>{about.text}</p>
      <a className="text-link" href="#consultation">
        Давайте обсудим вашу задачу <RiArrowRightLine />
      </a>
    </div>
    {about.image && (
      <div className="about-visual">
        <EquipmentImage
          src={about.image}
          alt={about.imageAlt || 'Оборудование, поставленное компанией «Дом Станков»'}
          fill
          sizes="(max-width: 700px) 100vw, 55vw"
        />
        <span>ОБОРУДОВАНИЕ. ЛЮДИ. РЕЗУЛЬТАТ.</span>
      </div>
    )}
  </section>
);
export const HomepageDeliveries = ({ cases }: Pick<HomepageContent, 'cases'>) => {
  const visible = cases.filter((item) => item.published && item.image);
  if (!visible.length) return null;
  return (
    <section className="deliveries-section container" id="deliveries">
      <div className="section-heading">
        <div>
          <p className="eyebrow">НАША РАБОТА В ДЕТАЛЯХ</p>
          <h2>От выбора — к поставке.</h2>
        </div>
        <p>
          Реальные станки.
          <br />
          Примеры выполненных поставок и оснастки.
        </p>
      </div>
      <div className="delivery-grid">
        {visible.map((item) => (
          <article className="delivery-card" key={item.id}>
            <div className="delivery-card__photo">
              <EquipmentImage
                src={item.image}
                alt={item.imageAlt || item.title}
                fill
                sizes="(max-width: 700px) 100vw, 33vw"
              />
            </div>
            <div className="delivery-card__copy">
              <p className="eyebrow">{item.category}</p>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              {item.result && <p className="delivery-card__result">{item.result}</p>}
              {item.sourceUrl && /^https?:\/\//.test(item.sourceUrl) && (
                <a className="text-link" href={item.sourceUrl} target="_blank" rel="noopener noreferrer">
                  Публикация о поставке ↗
                </a>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};
export const HomepageFaq = ({ faq }: Pick<HomepageContent, 'faq'>) =>
  !faq.length ? null : (
    <section className="homepage-faq container" id="questions">
      <div>
        <p className="eyebrow">ПЕРЕД ПОКУПКОЙ</p>
        <h2>Ответы на важное.</h2>
      </div>
      <div>
        {faq.map((item) => (
          <details key={item.id}>
            <summary>{item.question}</summary>
            <p>{item.answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
